import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Sparkles, Users } from 'lucide-react';

/**
 * When `?agent=CODE` is present on artist signup or login, show a short “podcast read”
 * style welcome + ticker so visitors know why they landed here.
 */
export default function AgentArtistInviteBand({ mode = 'signup' }) {
  const [searchParams] = useSearchParams();
  const agentCode = useMemo(() => {
    const raw = searchParams.get('agent')?.trim();
    if (!raw) return null;
    return raw.toUpperCase();
  }, [searchParams]);

  const [agentDisplayName, setAgentDisplayName] = useState(null);
  const [lookupDone, setLookupDone] = useState(false);

  useEffect(() => {
    if (!agentCode) {
      setLookupDone(true);
      setAgentDisplayName(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const codeSnap = await getDoc(doc(db, 'agent_codes', agentCode));
        if (!codeSnap.exists() || cancelled) {
          if (!cancelled) setAgentDisplayName(null);
          return;
        }
        const agentId = codeSnap.data()?.agent_id;
        if (!agentId || cancelled) return;
        const profileSnap = await getDoc(doc(db, 'profiles', agentId));
        if (!profileSnap.exists() || cancelled) return;
        const d = profileSnap.data();
        const name = d.artist_name || d.full_name || d.karaoke_name || null;
        if (!cancelled) setAgentDisplayName(name);
      } catch {
        if (!cancelled) setAgentDisplayName(null);
      } finally {
        if (!cancelled) setLookupDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentCode]);

  if (!agentCode) return null;

  const rep = agentDisplayName || 'your representative';
  const inviteLine = `You’re invited by ${rep} to join Your Song Request — turn live crowds into paid song requests.`;

  const tickerSegments = [
    `Invited by ${rep}`,
    'Paid song requests from your audience',
    'Go live from your phone',
    'Built for pubs, venues & working artists',
    `Agency code ${agentCode}`,
  ];

  const signupHref = `/signup?agent=${encodeURIComponent(agentCode)}`;

  return (
    <div className="mb-6 w-full space-y-4">
      <div
        className="relative overflow-hidden rounded-lg border border-amber-400/25 bg-gradient-to-r from-amber-950/80 via-amber-900/40 to-amber-950/80 py-2.5 shadow-[0_0_24px_rgba(251,191,36,0.12)]"
        aria-hidden
      >
        <div className="flex w-[200%] animate-ysr-agent-marquee flex-row">
          {[0, 1].map((copy) => (
            <div
              key={copy}
              className="flex w-1/2 shrink-0 items-center justify-around gap-8 px-4 sm:gap-12 sm:px-8"
            >
              {tickerSegments.map((text) => (
                <span
                  key={`${copy}-${text}`}
                  className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap font-semibold uppercase tracking-[0.2em] text-amber-100/95 text-[10px] sm:text-xs"
                >
                  <Sparkles className="h-3.5 w-3.5 text-amber-300 sm:h-4 sm:w-4" />
                  {text}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-gradient-to-br from-gray-900/95 to-gray-950/95 px-4 py-4 shadow-lg sm:px-5 sm:py-5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/30">
            <Users className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-200/90">
              {lookupDone && !agentDisplayName
                ? 'Artist invite link'
                : 'Your agent sent you here'}
            </p>
            <h2 className="text-lg font-bold leading-snug text-white sm:text-xl">
              Welcome — create your artist account
            </h2>
            <p className="text-sm leading-relaxed text-gray-300 sm:text-[0.9375rem]">{inviteLine}</p>
            {mode === 'login' && (
              <p className="pt-1 text-sm text-gray-400">
                New to the platform?{' '}
                <Link
                  to={signupHref}
                  className="font-semibold text-amber-300 underline decoration-amber-500/50 underline-offset-2 hover:text-amber-200"
                >
                  Complete signup with this invite
                </Link>
              </p>
            )}
            {mode === 'signup' && (
              <p className="text-xs text-gray-500">
                Already registered?{' '}
                <Link
                  to={`/login?agent=${encodeURIComponent(agentCode)}`}
                  className="font-medium text-amber-300/90 hover:text-amber-200"
                >
                  Log in here
                </Link>{' '}
                — we’ll keep this invite context in the URL.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
